import React from 'react';

/**
 * Image component that handles various image sources and CDN optimization
 * @param {Object} props - Component props
 * @param {string|Object} props.image - Can be a URL string, path string, or image object with cdnUrl/cdnPath
 * @param {number} props.width - Image width for CDN resizing
 * @param {number} props.height - Image height for CDN resizing
 * @param {string} props.alt - Alt text for accessibility
 * @param {string} props.className - CSS class name
 * @param {Object} props.style - Inline styles
 * @param {boolean} props.lazy - Enable lazy loading (default: true)
 * @param {string} props.fit - Image fit mode for CDN (cover, contain, fill, etc.)
 * @param {number} props.quality - Image quality for CDN (1-100)
 * @param {boolean} props.cacheBuster - Add cache buster parameter
 * @param {Function} props.onLoad - Image load callback
 * @param {Function} props.onError - Image error callback
 * @param {Object} props.imgProps - Additional props to pass to img element
 */
function Image({
    image,
    width,
    height,
    alt = '',
    className = '',
    style = {},
    lazy = true,
    fit,
    quality,
    cacheBuster = false,
    allowRedirect = true,
    onLoad,
    onError,
    ...imgProps
}) {
    // Determine the source URL based on the image prop type
    const getSrcUrl = () => {
        if (!image) return '';

        // If image is a string (URL or path)
        if (typeof image === 'string') {
            // If it's already a full URL, return as is
            if (image.startsWith('http://') || image.startsWith('https://')) {
                return image;
            }
            // If it's a path starting with /cdn/, treat as CDN path
            if (image.startsWith('/cdn/')) {
                return buildCdnUrl(image);
            }
            // Otherwise treat as regular path
            return image;
        }

        // If image is an object (File model instance)
        if (typeof image === 'object' && image !== null) {
            // Check for cdnUrl method/property first
            if (typeof image.cdnUrl === 'function') {
                return buildCdnUrl(image.cdnUrl());
            }
            if (typeof image.cdnUrl === 'string') {
                return buildCdnUrl(image.cdnUrl);
            }

            // Check for cdnPath method/property
            if (typeof image.cdnPath === 'function') {
                return buildCdnUrl(image.cdnPath());
            }
            if (typeof image.cdnPath === 'string') {
                return buildCdnUrl(image.cdnPath);
            }

            // Check for uuid and extension to build CDN path
            if (image.uuid && image.extension) {
                return buildCdnUrl(`/cdn/${image.uuid}.${image.extension}`);
            }
        }

        return '';
    };

    // Build CDN URL with query parameters
    const buildCdnUrl = (baseUrl) => {
        const params = new URLSearchParams();

        // Add dimensions
        if (width) params.set('w', width.toString());
        if (height) params.set('h', height.toString());

        // Add fit mode
        if (fit) params.set('fit', fit);

        // Add quality
        if (quality) params.set('q', quality.toString());

        // Add redirect parameter if allowRedirect is true
        if (allowRedirect) params.set('redirect', 'true');

        // Add etag if available on image object
        if (typeof image === 'object' && image?.etag) {
            params.set('etag', image.etag);
        }

        // Add cache buster
        if (cacheBuster) {
            params.set('cb', Date.now().toString());
        }

        // Return URL with parameters
        const paramString = params.toString();
        return paramString ? `${baseUrl}?${paramString}` : baseUrl;
    };

    const srcUrl = getSrcUrl();

    // Don't render if no valid source
    if (!srcUrl) {
        return null;
    }

    let combinedStyle = { ...style };

    // If width/height are provided, set them in style for better layout control
    if (width) combinedStyle.maxWidth = `${width}px`;
    if (height) combinedStyle.maxHeight = `${height}px`;

    return (
        <img
            src={srcUrl}
            alt={alt}
            className={className}
            style={combinedStyle}
            loading={lazy ? 'lazy' : 'eager'}
            onLoad={onLoad}
            onError={onError}
            {...imgProps}
        />
    );
}

export default Image;
